import { OrderRow } from "@features/orders-features/types/types";
import { Chip, Stack, Typography } from "@mui/material";
import StorefrontIcon from "@mui/icons-material/Storefront";
import { formatOrderDate } from "@features/orders-features/functions/formatOrderDate";
import {
  getInvoiceStatusChip,
  getOrderStatusChip,
} from "@features/orders-features/components/orderStatusChips";
import { CopyToClipboardIconButton } from "shared-ui/src/components/CopyToClipboardIconButton";
import { useClaims } from "../../../../../../../auth/ClaimsProvider";
import { pascalToTitleCase } from "@features/orders-features/functions/helpers";
import { Fragment, useMemo } from "react";
import { OrgUnitDto } from "shared-ui/src/features/storefront-feature";
import { FulfillmentStatus } from "@lib/ShiOneClient";
import { toFulfillmentStatus } from "@features/orders-features/functions/getFulfillmentStatus";

function OrderStatus({ status }: Readonly<{ status?: FulfillmentStatus }>) {
  const { color, icon, iconColor, borderColor } = getOrderStatusChip(status);

  if (!status) {
    return null;
  }

  return (
    <Chip
      icon={icon}
      label={pascalToTitleCase(status ?? "")}
      size="small"
      variant="outlined"
      sx={{
        typography: "caption",
        borderColor: borderColor ?? color,
        color,
        "& .MuiChip-icon": { color: iconColor ?? color },
      }}
    />
  );
}

function InvoiceStatus({ status }: Readonly<{ status?: string }>) {
  const { icon: Icon, borderColor, iconColor } = getInvoiceStatusChip(status);

  if (!status) {
    return null;
  }

  return (
    <Chip
      icon={Icon ? <Icon /> : undefined}
      label={status}
      size="small"
      variant="outlined"
      sx={{
        typography: "caption",
        borderColor,
        color: "text.primary",
        "& .MuiChip-icon": {
          color: iconColor,
          width: 16,
          height: 16,
          marginLeft: "8px",
          marginRight: "2px",
        },
        "& .MuiChip-label": {
          paddingLeft: "2px",
          paddingRight: "8px",
        },
      }}
    />
  );
}

export function PurchaseNumber({ po }: Readonly<{ po?: string }>) {
  if (!po) {
    return null;
  }
  return (
    <Chip
      label={
        <Stack direction="row" spacing={0.5} alignItems="center">
          <Typography variant="caption" component="span" color="textSecondary">
            PO#
          </Typography>
          <Typography variant="caption" component="span" color="textPrimary">
            {po}
          </Typography>
          <CopyToClipboardIconButton
            value={po}
            label="Copy PO Number"
            sx={{ color: "action.active" }}
          />
        </Stack>
      }
      variant="outlined"
      size="small"
      sx={{
        "& .MuiChip-label": {
          display: "flex",
          alignItems: "center",
          paddingRight: "2px",
        },
      }}
    />
  );
}

export function OrderDrawerHeader({
  order,
  orgUnits,
}: Readonly<{ order?: OrderRow | null; orgUnits: OrgUnitDto[] }>) {
  const { getClaimValue } = useClaims();

  const crmAccountIdCurrent = getClaimValue("crmAccountId");
  const selectedCrmAccountId = order?.crmAccountId ?? crmAccountIdCurrent;

  const orgUnitName = useMemo(
    () =>
      orgUnits.find((p) => p.crmAccountId === selectedCrmAccountId)
        ?.displayName,
    [orgUnits, selectedCrmAccountId],
  );

  const subtitle = useMemo(() => {
    const items: Array<{
      key: string;
      value: string;
      withStorefront?: boolean;
    }> = [
      ...(orgUnitName
        ? [{ key: "org-unit", value: orgUnitName, withStorefront: true }]
        : []),
      {
        key: "ordered-on",
        value: `Ordered on ${formatOrderDate(order?.orderDate)}`,
      },
      { key: "ordered-by", value: `Ordered by ${order?.orderedBy || "—"}` },
    ];

    return items.filter((item) => item.value.trim().length > 0);
  }, [orgUnitName, order?.orderDate, order?.orderedBy]);

  if (!order) {
    return null;
  }

  const hasSalesOrderIdentifier = Boolean(order.salesOrder || order.biOrderId);
  const orderIdentifier = order.salesOrder || order.biOrderId || order.webOrderId;

  return (
    <Stack
      direction="column"
      alignItems="flex-start"
      justifyContent="flex-start"
      spacing={0.5}
    >
      <Stack
        direction="row"
        spacing={1}
        alignItems="center"
        role="group"
        aria-label="Order header title"
      >
        <Typography variant="body1" fontWeight="normal" noWrap>
          {[FulfillmentStatus.AwaitingApproval, FulfillmentStatus.TransferApprover].includes(
            toFulfillmentStatus(order.status),
          )
            ? hasSalesOrderIdentifier
              ? orderIdentifier
              : `Web Order #${orderIdentifier}`
            : hasSalesOrderIdentifier
              ? `SO# ${orderIdentifier}`
              : `Web Order #${orderIdentifier}`}
        </Typography>
        <PurchaseNumber po={order.purchaseOrder} />
        <OrderStatus status={order.status as FulfillmentStatus | undefined} />
        <InvoiceStatus status={order.invoiceStatus || undefined} />
      </Stack>
      <Stack
        direction="row"
        spacing={0.5}
        alignItems="center"
        role="group"
        aria-label="Order header subtitle"
      >
        {subtitle.map((item, index) => (
          <Fragment key={`order-header-subtitle-${item.key}`}>
            <Typography
              component="span"
              variant="body2"
              color="textSecondary"
              noWrap
            >
              {item.withStorefront && (
                <StorefrontIcon
                  fontSize="small"
                  sx={{ pr: 0.5 }}
                  aria-hidden="true"
                />
              )}
              {item.value}
            </Typography>
            {index < subtitle.length - 1 && (
              <Typography
                component="span"
                variant="body2"
                color="textPrimary"
                sx={{ fontWeight: "bold" }}
                aria-hidden="true"
              >
                {" \u00b7 "}
              </Typography>
            )}
          </Fragment>
        ))}
      </Stack>
    </Stack>
  );
}
